import React, { useRef, useState } from 'react';
import css from 'styled-jsx/css';
import { colors } from '../Theme';

import Button from './Button.js';
import Form from '../../common/forms/Form';
import PropTypes from 'prop-types';
import FormControl from './FormControl';
import { validate, strings } from '../../common/forms/Validations';

import CloseIcon from '../../../../public/static/images/icons/close.svg';

import support from '../../../services/api/support';
import SupportFormToggle from './SupportFormToggle';

const SUPPORT_FORM_TOGGLE_ID = 'support-form-toggle';

const formButtonStyles = css.resolve`
  .button {
    margin-bottom: 20px;
    width: 100%;
  }
`;

const supportFormStyles = css`
  .support-form-toggle {
    display: none;
  }
  .support-form {
    position: fixed;
    display: none;
    padding: 10px 40px;
    right: 10px;
    max-width: 400px;
    width: 100%;
    background-color: white;
    flex-direction: column;
    bottom: 0;
    background: ${colors.ywgPaleGreyTwo} url('/static/images/plane-1.svg') 90%
      bottom no-repeat;
    box-shadow: 5px 5px 16px 0 rgba(121, 121, 121, 0.3);
    box-sizing: border-box;
    z-index: 1000;
  }
  .support-form-toggle:checked + .support-form {
    display: flex;
  }
  .message-sent {
    padding: 30px 20px;
    font-size: 1.2rem;
  }
  @media (max-width: 600px) {
    .support-form {
      top: 0;
      right: 0;
      left: 0;
      max-width: initial;
      overflow: scroll;
      z-index: 400;
    }
  }

  .support-close-button {
    position: absolute;
    top: 0;
    right: 0;
    padding: 15px;
    background: none;
    border: none;
    cursor: pointer;
  }
`;
const validators = () => {
  return {
    name: [strings.required],
    email: [strings.required],
    subject: [strings.required],
    message: [strings.required]
  };
};

const ErrorMessage = ({ subject, message }) => {
  const mailto = `mailto:help@yupwego.com?subject=[Support] ${subject}&body=${message}`;
  return (
    <p>
      Une erreur s’est produite lors de l’envoi du message. Transmettez-nous
      votre demande directement par mail à l’adresse{' '}
      <a href={mailto}>help@yupwego.com</a>
    </p>
  );
};

ErrorMessage.propTypes = {
  subject: PropTypes.string,
  message: PropTypes.string
};

const SupportForm = ({ fixedButtonDisabled }) => {
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [readyToPost, setReadyToPost] = useState(true);
  const [postError, setPostError] = useState(false);
  const nameInputRef = useRef(null);
  const initialValues = {
    name: '',
    email: '',
    subject: '',
    message: ''
  };

  const onCheckboxChange = () => {
    if (checkboxChecked === false) {
      nameInputRef.current.focus();
    }
    setCheckboxChecked(!checkboxChecked);
  };

  const formSubmitted = async (supportForm, action) => {
    action.setSubmitting(true);
    const res = await support.createSupportMessage(supportForm);
    if (res.ok) {
      setReadyToPost(!readyToPost);
      !postError || setPostError(false);
      setTimeout(() => {
        setCheckboxChecked(false);
        setReadyToPost(true);
      }, 5000);
    } else {
      setPostError(true);
      action.setSubmitting(false);
    }
  };

  return (
    <>
      {!fixedButtonDisabled ? <SupportFormToggle /> : null}
      <input
        type="checkbox"
        id={SUPPORT_FORM_TOGGLE_ID}
        className="support-form-toggle"
        checked={checkboxChecked}
        onChange={onCheckboxChange}
      />
      <Form
        initialValues={initialValues}
        validate={value => validate(value, validators())}
        onSubmit={formSubmitted}
      >
        {({
          values,
          metas,
          onChange,
          onBlur,
          doSubmit,
          submitting,
          ...form
        }) => (
          <form className="support-form" onSubmit={doSubmit}>
            <label
              htmlFor={SUPPORT_FORM_TOGGLE_ID}
              className="support-close-button"
            >
              <CloseIcon />
              <span className="sr-only">Fermer le formulaire de support</span>
            </label>
            {readyToPost ? (
              <>
                <h2>Besoin d&apos;aide&nbsp;?</h2>
                <FormControl
                  type="text"
                  name="name"
                  label="Nom"
                  value={values.name}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={metas.name.error}
                  valid={metas.name.valid}
                  ref={nameInputRef}
                  required
                />
                <FormControl
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="contact@example.com"
                  value={values.email}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={metas.email.error}
                  valid={metas.email.valid}
                  required
                />
                <FormControl
                  type="text"
                  name="subject"
                  label="Sujet"
                  value={values.subject}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={metas.subject.error}
                  valid={metas.subject.valid}
                  required
                />
                <FormControl
                  id="message"
                  name="message"
                  label="Message"
                  type="textarea"
                  rows="3"
                  value={values.message}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={metas.message.error}
                  valid={metas.message.valid}
                  required
                />
              </>
            ) : (
              <p className="message-sent">
                Votre message a bien été envoyé. Notre équipe vous contactera
                dans les plus brefs délais.
              </p>
            )}
            {postError ? (
              <ErrorMessage subject={values.subject} message={values.message} />
            ) : null}
            {readyToPost ? (
              <Button
                type="submit"
                submitting={submitting}
                className={formButtonStyles.className}
              >
                Envoyer
              </Button>
            ) : null}
          </form>
        )}
      </Form>
      <style jsx>{supportFormStyles}</style>
      {formButtonStyles.styles}
    </>
  );
};

SupportForm.defaultProps = {
  fixedButtonDisabled: false
};

SupportForm.propTypes = {
  fixedButtonDisabled: PropTypes.bool
};

export default SupportForm;
export { SUPPORT_FORM_TOGGLE_ID };
